import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  Typography,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { Department } from '../../api/api-generated';
import {
  useFindAllCompaniesQuery,
  useFindAlldepartmentsQuery,
  useFindAllOrdersQuery,
  useFindOneCompanyQuery,
  useFindOneHardwareQuery,
  useGetAllTypesQuery,
  useRemoveHardwareMutation,
} from '../../api/api-hooks';
import DeleteItemDialog from '../../components/DeleteItemDialog';
import EditItemDialog from '../../components/EditItemDialog';
import { truncateISOString } from '../../utils/dateTime';
import { Item } from '../../utils/sharedTypes';
import { nullStringCheck } from '../../utils/stringManipulation';
import AnalyzerTypeManagementButton from '../analyzer-types/AnalyzerTypeManagementButton';
import DepartmentsButton from '../departments/DepartmentsButton';
import HardwareManagementButton from '../hardware/HardwareManagementButton';
import { HardwareItemForm } from '../hardware/hardwareTypes';
import {
  hardwareHandlerMiddleware,
  hardwareValidator,
} from '../hardware/hardwareUtils';
import useHardwareItemUpdater from '../hardware/useHardwareItemUpdater';
import ImportButton from '../import-licenses/ImportButton';
import SoftwareSoldCreatorWithDefaults from '../software-sold/SoftwareSoldCreatorWithDefaults';
import SystemModulesButton from '../system-module-licenses/SystemModulesButton';
import AdminCompanyHardwareTable from './AdminCompanyHardwareTable';
import AdminLicenseDownloadForm from './AdminLicenseDownloadForm';

const LptCompanyPage = () => {
  const { pathname } = useLocation();
  // Extract company ID from pathname
  const companyId = pathname.slice(pathname.lastIndexOf('/') + 1);
  const [selectedHardware, setSelectedHardware] = useState<Item[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [softwareDialogOpen, setSoftwareDialogOpen] = useState(false);
  const [hardwareDeleter] = useRemoveHardwareMutation();
  const hardwareUpdater = useHardwareItemUpdater();
  const { data: company } = useFindOneCompanyQuery({
    id: Number(companyId),
  });
  const { data: analyzerTypes } = useGetAllTypesQuery({});
  const { data: orders } = useFindAllOrdersQuery({});
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: departments } = useFindAlldepartmentsQuery({});
  if (
    company === undefined ||
    companies === undefined ||
    analyzerTypes === undefined ||
    orders === undefined ||
    departments === undefined
  )
    return <CircularProgress />;

  const filteredOrders = orders.filter((order) => !order.completed);
  return (
    <Grid container direction='column' alignItems='flex-start' spacing={2}>
      <Grid item>
        <Typography variant='h2'>{company.companyName}</Typography>
      </Grid>
      <Grid container item alignItems='center' spacing={1}>
        <Grid item>
          <AdminLicenseDownloadForm company={company} />
        </Grid>
        <Grid item>
          <HardwareManagementButton company={company} />
        </Grid>
        <Grid item>
          <ImportButton company={company} />
        </Grid>
        <Grid item>
          <SystemModulesButton company={company} />
        </Grid>
        {selectedHardware.length > 0 && (
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                setDeleteModalOpen(true);
              }}
            >
              Delete Hardware
            </Button>
          </Grid>
        )}
        {selectedHardware.length === 1 && (
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                setEditModalOpen(true);
              }}
            >
              Edit Hardware
            </Button>
          </Grid>
        )}
        <Grid item>
          <DepartmentsButton company={company} />
        </Grid>
        <Grid item>
          <ToggleButton
            value='check'
            selected={editMode}
            onChange={() => setEditMode((state) => !state)}
          >
            Edit Mode
          </ToggleButton>
        </Grid>
      </Grid>
      <Grid item>
        <Typography color='error' variant='body2'>
          A grayed-out software icon indicates no active support plan.
        </Typography>
      </Grid>
      <Grid item style={{ width: '100%' }}>
        <AdminCompanyHardwareTable
          company={company}
          setSelectedHardware={setSelectedHardware}
          editMode={editMode}
        />
      </Grid>
      <DeleteItemDialog
        deleteModalOpen={deleteModalOpen}
        setDeleteModalOpen={setDeleteModalOpen}
        itemDeleter={hardwareDeleter}
        itemsToDelete={selectedHardware}
        setSelectedItems={setSelectedHardware}
        itemName={'Hardware'}
      />
      {selectedHardware.length > 0 && (
        <EditItemDialog
          editModalOpen={editModalOpen}
          setEditModalOpen={setEditModalOpen}
          itemUpdater={hardwareUpdater}
          itemGetter={useFindOneHardwareQuery}
          itemId={selectedHardware[0].id}
          handlerMiddleware={hardwareHandlerMiddleware}
          itemValidator={hardwareValidator}
          formInstructions='Serial #, Hardware Type, Company, and Firmware are required.'
          itemName='Hardware'
          children={(addFormState: HardwareItemForm, handler: any) => (
            <Grid container spacing={3}>
              <Grid container item xs={6} direction='column' spacing={1}>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='serial'
                    label='Serial #'
                    fullWidth
                    value={nullStringCheck(addFormState.serial)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <Autocomplete
                    onChange={(event, value) => {
                      handler(event, value, 'hardwareType');
                    }}
                    value={addFormState.hardwareType}
                    options={analyzerTypes}
                    getOptionLabel={(type) => type.typeName}
                    style={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Type'
                        variant='outlined'
                      />
                    )}
                  />
                </Grid>

                <Grid item>
                  <AnalyzerTypeManagementButton />
                </Grid>
                {addFormState.companies.length > 0 ? (
                  addFormState.companies.map((company, idx) => (
                    <Grid item>
                      <Autocomplete
                        onChange={(event, value) => {
                          handler(event, value, `companies${idx}`);
                        }}
                        value={company}
                        options={companies}
                        getOptionLabel={(company) => company.companyName}
                        style={{ width: 300 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Select Company'
                            variant='outlined'
                          />
                        )}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, 'companies0');
                      }}
                      value={addFormState.companies[0]}
                      options={companies}
                      getOptionLabel={(company) => company.companyName}
                      style={{ width: 300 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Company'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Autocomplete
                    onChange={(event, value) => {
                      handler(event, value, 'department');
                    }}
                    value={addFormState.department}
                    options={departments.filter((department: Department) =>
                      addFormState.companies.some(
                        (company) => company.id === department?.companyId
                      )
                    )}
                    getOptionLabel={(department) => department.departmentName}
                    style={{ width: 300 }}
                    disabled={
                      !addFormState.companies ||
                      addFormState.companies.length < 1
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Department'
                        variant='outlined'
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='firmware'
                    label='Firmware'
                    fullWidth
                    value={nullStringCheck(addFormState.firmware)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='manufacturer'
                    label='Manufacturer'
                    fullWidth
                    value={nullStringCheck(addFormState.manufacturer)}
                    onChange={handler}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={6} direction='column' spacing={1}>
                <Grid item>
                  <Autocomplete
                    onChange={(event, value) => {
                      handler(event, value, 'order');
                    }}
                    value={addFormState.order}
                    options={filteredOrders}
                    getOptionLabel={(order) =>
                      order.purchaseOrderName
                        ? order.purchaseOrderName
                        : order.orderDate
                    }
                    style={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Order'
                        variant='outlined'
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>
                      <Typography>Warranty Start: </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type='date'
                        margin='dense'
                        name='warrantyStartDate'
                        value={truncateISOString(
                          nullStringCheck(addFormState.warrantyStartDate)
                        )}
                        onChange={handler}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container alignItems='center' spacing={1}>
                    <Grid item>
                      <Typography>Warranty End: </Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        type='date'
                        margin='dense'
                        name='warrantyEndDate'
                        value={truncateISOString(
                          nullStringCheck(addFormState.warrantyEndDate)
                        )}
                        onChange={handler}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='trackingNumber'
                    label='Tracking #'
                    fullWidth
                    value={nullStringCheck(addFormState.trackingNumber)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    margin='dense'
                    name='shipper'
                    label='Shipper'
                    fullWidth
                    value={nullStringCheck(addFormState.shipper)}
                    onChange={handler}
                  />
                </Grid>
                <Grid item container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Did LPT sell the hardware?</Typography>
                  </Grid>
                  <Grid item>
                    <Select
                      value={addFormState.lptSold}
                      onChange={handler}
                      name='lptSold'
                    >
                      <MenuItem value='true'>Yes</MenuItem>
                      <MenuItem value='false'>No</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2'>
                      This dialog creates a new hardware item marked sold by LPT
                      without affecting Inventory. If you want to mark inventory
                      as sold, please do so from the Inventory tab.
                    </Typography>
                  </Grid>
                </Grid>
                <SoftwareSoldCreatorWithDefaults
                  company={addFormState.companies[0]}
                  serial={addFormState.serial}
                  dialogOpen={softwareDialogOpen}
                  setDialogOpen={setSoftwareDialogOpen}
                />
              </Grid>
            </Grid>
          )}
        />
      )}
    </Grid>
  );
};

export default LptCompanyPage;
